import Locale from "./Locale.mts";

/**
 * Resolves agreement/disclosure paths (e.g. `/terms.pdf`) to their localized
 * variant for the active locale.
 *
 * These paths are redirect keys served by the web proxy, which registers a
 * `<locale>/`-prefixed key alongside the unprefixed one for docs that have a
 * localized variant. Deriving the prefix here keeps the paths out of the
 * translation catalogs — a translated path is a broken link, since the proxy
 * matches keys exactly and has no fallback.
 *
 * Only docs listed in `localized` have a non-English variant; prefixing anything
 * else would 404. `es` mirrors `withLocalizedAssets` in the US web app's proxy,
 * and `fr` the paths fr_CA.po already shipped translations for — those two are
 * the source of truth for what's actually uploaded per locale.
 */
namespace DocPath {
  const localized = {
    fr: new Set([
      "/autopay-agreement.pdf",
      "/e-sign.pdf",
      "/privacy-policy.pdf",
      "/terms.pdf",
    ]),
    es: new Set([
      "/ach-authorization.pdf",
      "/aig-identity-theft-and-fraud-claim-form.pdf",
      "/aig-summary-of-identity-fraud-benefits.pdf",
      "/autopay-agreement.pdf",
      "/debt-negotiation-terms-of-service.pdf",
      "/dispute-terms.pdf",
      "/do-not-sell.pdf",
      "/e-sign.pdf",
      "/kikoff-communication-authorization-pra.pdf",
      "/pennsylvania-consumers-disclosure.pdf",
      "/privacy-policy.pdf",
      "/secured-card-direct-deposit-terms.pdf",
      "/secured-card-funds-availability-policy.pdf",
      "/secured-card-merchant-cashback-addendum.pdf",
      "/secured-card-terms.pdf",
      "/subscription-cancellation-terms.pdf",
      "/terms.pdf",
      "/bill-negotiation-terms.pdf",
    ]),
  } as Partial<Record<Locale, Set<string>>>;

  export function localize(path: string, locale = Locale.config.current) {
    if (!localized[locale]?.has(path)) return path;

    return `/${locale}${path}`;
  }
}

export { DocPath };
export default DocPath;
